/**
 * Web Vitals reporter (RFC M Phase 3.1b).
 *
 * Captures Core Web Vitals (LCP, INP, CLS) + adjacent metrics (FCP,
 * TTFB) using `next/web-vitals` and forwards them to console in dev.
 *
 * Production path is intentionally a stub today: the web app has no
 * error / performance vendor (Sentry was installed but never
 * initialised, and removed 2026-10 — RFC launch-readiness-2026-10).
 * Collecting vitals means extending `/api/storefront/track` to accept a
 * `web_vital` event; until then this is a no-op in prod. The hook is
 * mounted here so that wiring is one line away.
 *
 * Usage: import + render `<WebVitalsReporter />` once in the root
 * layout. Internally uses `useReportWebVitals` which Next.js calls
 * with each metric exactly once per page session.
 *
 * What you get in DevTools console (dev only):
 *   [web-vitals] LCP=1234ms (good)
 *   [web-vitals] CLS=0.05 (good)
 *   [web-vitals] INP=180ms (good)
 *
 * Thresholds match Google's "good / needs improvement / poor" rubric:
 *   LCP < 2500ms good, < 4000ms needs improvement, else poor
 *   INP < 200ms,  < 500ms,  else poor
 *   CLS < 0.1,    < 0.25,   else poor
 *   FCP < 1800ms, < 3000ms, else poor
 *   TTFB < 800ms, < 1800ms, else poor
 */
'use client'

import { useReportWebVitals } from 'next/web-vitals'

type Bucket = 'good' | 'needs-improvement' | 'poor'

const THRESHOLDS: Record<string, { good: number; ni: number; unit: string }> = {
  LCP: { good: 2500, ni: 4000, unit: 'ms' },
  INP: { good: 200, ni: 500, unit: 'ms' },
  FID: { good: 100, ni: 300, unit: 'ms' },
  CLS: { good: 0.1, ni: 0.25, unit: '' },
  FCP: { good: 1800, ni: 3000, unit: 'ms' },
  TTFB: { good: 800, ni: 1800, unit: 'ms' },
}

function bucket(name: string, value: number): Bucket {
  const t = THRESHOLDS[name]
  if (!t) return 'good'
  if (value < t.good) return 'good'
  if (value < t.ni) return 'needs-improvement'
  return 'poor'
}

export function WebVitalsReporter() {
  useReportWebVitals((metric) => {
    if (process.env.NODE_ENV === 'development') {
      const t = THRESHOLDS[metric.name]
      const unit = t?.unit ?? ''
      const formatted = unit === 'ms' ? Math.round(metric.value) : metric.value.toFixed(3)
      const status = bucket(metric.name, metric.value)
      // Coloured badge in DevTools — green / yellow / red so a bad
      // score visually pops without reading the value.
      const colour =
        status === 'good' ? '#16a34a' : status === 'needs-improvement' ? '#ca8a04' : '#dc2626'
       
      console.info(
        `%c[web-vitals]%c ${metric.name}=${formatted}${unit} %c${status}`,
        'color:#64748b;font-weight:bold',
        '',
        `color:white;background:${colour};padding:1px 6px;border-radius:3px;font-weight:bold`,
      )
      return
    }

    // Production reporter hook (no-op for now — see file header).
    // When /api/storefront/track accepts vitals:
    //   navigator.sendBeacon('/api/storefront/track', JSON.stringify({ kind: 'web_vital', name: metric.name, value: metric.value, id: metric.id, rating: bucket(metric.name, metric.value) }))
  })

  return null
}
